import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Select,
  DatePicker,
  InputNumber,
  Table,
  Space,
  Card,
  Row,
  Col,
  Typography,
  message,
  AutoComplete,
  Modal,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  UploadOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { createSalesOrder, updateSalesOrder, getSalesOrderById } from "../../../redux/actions/sale/salesOrderAction";
import { loadAllCustomersForSearch } from "../../../redux/actions/customer/getCustomerAction";
import { loadAllStaff } from "../../../redux/actions/user/getStaffAction";
import { loadProduct } from "../../../redux/actions/product/getAllProductAction";
import { globalProductSearch } from "../../../redux/actions/product/globalSearchAction";
import { loadAllBookPublisher } from "../../../redux/actions/bookPublisher/getBookPublisherAction";
import ProductSearchModal from "../ProductSearchModal";
import AddBookPublisher from "../../bookPublisher/addBookPublisher";
import CustomerPOUpload from "./CustomerPOUpload";
import store from "../../../redux/root";
import axios from "axios";

const { Text } = Typography;

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AddSalesOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;

  const [form] = Form.useForm();
  const [orderDate, setOrderDate] = useState(moment());
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [productSearchVisible, setProductSearchVisible] = useState(false);
  const [isbnInput, setIsbnInput] = useState("");
  const [isbnSearchResults, setIsbnSearchResults] = useState([]);
  const [publisherModalVisible, setPublisherModalVisible] = useState(false);
  const [publisherSearchValue, setPublisherSearchValue] = useState("");
  const [currentProductKey, setCurrentProductKey] = useState(null);
  const [customerPOUploadVisible, setCustomerPOUploadVisible] = useState(false);

  const allCustomers = useSelector((state) => state.customers.searchList || state.customers.list) || [];
  const allStaff = useSelector((state) => state.users.list) || [];
  const allProducts = useSelector((state) => state.products.list) || [];
  const allPublishers = useSelector((state) => state.bookPublishers?.list) || [];
  const currentOrder = useSelector((state) => state.salesOrders.currentOrder);

  // Refresh publishers when list updates and select newly added publisher
  useEffect(() => {
    if (!publisherModalVisible && publisherSearchValue && currentProductKey !== null) {
      // Publisher modal was just closed, try to find and select the publisher
      const addedPublisher = allPublishers.find(p => 
        p.name.toLowerCase().trim() === publisherSearchValue.toLowerCase().trim()
      );
      
      if (addedPublisher) {
        const updated = selectedProducts.map((p) => {
          if (p.key === currentProductKey) {
            return {
              ...p,
              book_publisher_id: addedPublisher.id,
              book_publisher: addedPublisher,
            };
          }
          return p;
        });
        setSelectedProducts(updated);
        setPublisherSearchValue("");
        setCurrentProductKey(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPublishers, publisherModalVisible]);

  useEffect(() => {
    dispatch(loadAllCustomersForSearch({ status: true }));
    dispatch(loadAllStaff({ status: true }));
    dispatch(loadProduct({ page: 1, limit: 10000 }));
    dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));

    // Check if coming from sales order selection
    const params = new URLSearchParams(location.search);
    const salesOrderId = params.get("sales_order_id");
    if (salesOrderId && !isEdit) {
      dispatch(getSalesOrderById(salesOrderId)).then((result) => {
        if (result?.order) {
          prefillFromOrder(result.order);
        }
      });
    }

    if (isEdit) {
      dispatch(getSalesOrderById(id)).then((result) => {
        if (result?.order) {
          loadOrderData(result.order);
        }
      });
    }
  }, [dispatch, id, isEdit, location.search]);

  const prefillFromOrder = (order) => {
    if (order) {
      form.setFieldsValue({
        customer_id: order.customer_id,
        user_id: order.user_id,
        discount: order.discount,
        note: order.note,
      });
      setOrderDate(moment(order.order_date));
      setDiscount(order.discount || 0);
      
      const billDiscountPercent = order.discount || 0;
      const products = order.order_items?.map((item, index) => {
        // Recalculate total_amount with current bill discount
        const itemTotal = item.sale_price * item.ordered_quantity;
        const itemDiscount = (itemTotal * (item.discount || 0)) / 100;
        const itemAfterProductDiscount = itemTotal - itemDiscount;
        const billDiscountAmount = (itemAfterProductDiscount * billDiscountPercent) / 100;
        
        return {
          key: index,
          id: item.product_id,
          name: item.product?.name || "",
          isbn: item.product?.isbn || "",
          author: item.product?.author || "",
          book_publisher_id: item.product?.book_publisher_id || null,
          book_publisher: item.product?.book_publisher || null,
          ordered_quantity: item.ordered_quantity,
          fulfilled_quantity: item.fulfilled_quantity || 0,
          sale_price: item.sale_price,
          discount: item.discount || 0,
          total_amount: itemAfterProductDiscount - billDiscountAmount,
        };
      }) || [];
      setSelectedProducts(products);
      calculateTotal(products, billDiscountPercent);
    }
  };

  const loadOrderData = (order) => {
    form.setFieldsValue({
      order_number: order.order_number,
      customer_id: order.customer_id,
      user_id: order.user_id,
      discount: order.discount,
      note: order.note,
    });
    setOrderDate(moment(order.order_date));
    setDiscount(order.discount || 0);
    
    const billDiscountPercent = order.discount || 0;
    const products = order.order_items?.map((item, index) => {
      // Recalculate total_amount with current bill discount
      const itemTotal = item.sale_price * item.ordered_quantity;
      const itemDiscount = (itemTotal * (item.discount || 0)) / 100;
      const itemAfterProductDiscount = itemTotal - itemDiscount;
      const billDiscountAmount = (itemAfterProductDiscount * billDiscountPercent) / 100;
      
      return {
        key: index,
        id: item.product_id,
        name: item.product?.name || "",
        isbn: item.product?.isbn || "",
        author: item.product?.author || "",
        book_publisher_id: item.product?.book_publisher_id || null,
        book_publisher: item.product?.book_publisher || null,
        ordered_quantity: item.ordered_quantity,
        fulfilled_quantity: item.fulfilled_quantity || 0,
        sale_price: item.sale_price,
        discount: item.discount || 0,
        total_amount: itemAfterProductDiscount - billDiscountAmount,
      };
    }) || [];
    setSelectedProducts(products);
    calculateTotal(products, billDiscountPercent);
  };

  const handleAddProduct = (product) => {
    const existingIndex = selectedProducts.findIndex((p) => p.id === product.id);
    if (existingIndex >= 0) {
      message.warning("Product already added");
      return;
    }

    const itemTotal = (product.sale_price || 0) * 1;
    const itemDiscount = (itemTotal * 0) / 100; // No product discount initially
    const itemAfterProductDiscount = itemTotal - itemDiscount;
    const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
    
    const newProduct = {
      key: selectedProducts.length,
      id: product.id,
      name: product.name,
      isbn: product.isbn,
      author: product.author || "",
      book_publisher_id: product.book_publisher_id || null,
      book_publisher: product.book_publisher || null,
      ordered_quantity: 1,
      fulfilled_quantity: 0,
      sale_price: product.sale_price || 0,
      discount: 0,
      total_amount: itemAfterProductDiscount - billDiscountAmount,
    };

    const updated = [...selectedProducts, newProduct];
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
    setProductSearchVisible(false);
  };

  // Fetch next temporary ISBN from server (fallback to client-only calculation if server fails)
  const fetchNextTempISBN = async () => {
    try {
      const resp = await axios.get("/v1/utils/next-temp-isbn");
      if (resp && resp.data && resp.data.tempIsbn) {
        return resp.data.tempIsbn;
      }
    } catch (e) {
      // ignore and fall back to client-only generation
    }

    // Combine selectedProducts and allProducts to find the maximum NA used
    const candidates = [
      ...selectedProducts.map((p) => p.isbn).filter(Boolean),
      ...allProducts.map((p) => p.isbn).filter(Boolean),
    ];

    const existingTempISBNs = candidates
      .filter((isbn) => isbn && isbn.startsWith("NA"))
      .map((isbn) => {
        const match = isbn.match(/^NA(\d+)$/i);
        return match ? parseInt(match[1]) : 0;
      });

    const maxNum = existingTempISBNs.length > 0 ? Math.max(...existingTempISBNs) : 0;
    return `NA${maxNum + 1}`;
  };

  // Handler for adding a new product by ISBN (when ISBN doesn't exist in database)
  const handleAddNewProductByISBN = (isbn) => {
    if (!isbn || !isbn.trim()) return;
    
    // Check if this ISBN already exists in the list
    const existingProduct = selectedProducts.find(prod => prod.isbn === isbn.trim());
    if (existingProduct) {
      message.warning("Product with this ISBN already added");
      return;
    }

    // Create new product object for manual entry
    const itemTotal = 0 * 1; // sale_price is 0 initially
    const itemDiscount = (itemTotal * 0) / 100;
    const itemAfterProductDiscount = itemTotal - itemDiscount;
    const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
    
    const newProduct = {
      key: selectedProducts.length,
      isbn: isbn.trim(),
      name: "",
      author: "",
      book_publisher_id: null,
      id: null, // No product_id for new products
      ordered_quantity: 1,
      fulfilled_quantity: 0,
      sale_price: 0,
      discount: 0,
      total_amount: itemAfterProductDiscount - billDiscountAmount,
      isNewProduct: true, // Flag to indicate this is a new product
      hasTemporaryISBN: isbn.trim().startsWith('NA'), // Check if it's a temporary ISBN
    };

    const updated = [...selectedProducts, newProduct];
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
    setIsbnInput("");
  };

  // Handler for adding a new product without ISBN
  const handleAddProductWithoutISBN = async () => {
    const tempISBN = await fetchNextTempISBN();
    
    // Create new product object for manual entry without ISBN
    const itemTotal = 0 * 1; // sale_price is 0 initially
    const itemDiscount = (itemTotal * 0) / 100;
    const itemAfterProductDiscount = itemTotal - itemDiscount;
    const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
    
    const newProduct = {
      key: selectedProducts.length,
      isbn: tempISBN,
      name: "",
      author: "",
      book_publisher_id: null,
      id: null, // No product_id for new products
      ordered_quantity: 1,
      fulfilled_quantity: 0,
      sale_price: 0,
      discount: 0,
      total_amount: itemAfterProductDiscount - billDiscountAmount,
      isNewProduct: true,
      hasTemporaryISBN: true, // Flag to indicate temporary ISBN
    };

    const updated = [...selectedProducts, newProduct];
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
    message.info(`Product added with temporary ISBN: ${tempISBN}. You can update it later when the actual ISBN is available.`);
  };

  // Handler for updating new product fields
  const handleUpdateNewProductField = (key, field, value) => {
    const updated = selectedProducts.map((p) => {
      if (p.key === key) {
        const updatedProduct = { ...p, [field]: value };
        // Recalculate total_amount if price or quantity changed
        if (field === "sale_price" || field === "ordered_quantity" || field === "discount") {
          const itemTotal = updatedProduct.sale_price * updatedProduct.ordered_quantity;
          const itemDiscount = (itemTotal * (updatedProduct.discount || 0)) / 100;
          const itemAfterProductDiscount = itemTotal - itemDiscount;
          const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
          updatedProduct.total_amount = itemAfterProductDiscount - billDiscountAmount;
        }
        return updatedProduct;
      }
      return p;
    });
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
  };

  const handlePublisherSearch = (value, productKey) => {
    setPublisherSearchValue(value);
    setCurrentProductKey(productKey);
  };

  const handleAddPublisher = (productKey) => {
    setCurrentProductKey(productKey);
    setPublisherModalVisible(true);
  };

  const handlePublisherAdded = async () => {
    // Reload publishers list
    await dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));
    
    // Close modal - useEffect will handle selecting the publisher
    setPublisherModalVisible(false);
    message.success("Publisher added successfully!");
  };

  // Handler for customer PO data extraction
  const handleCustomerPODataExtracted = async (poData) => {
    try {
      // Reload customers and publishers to ensure we have latest data
      await dispatch(loadAllCustomersForSearch({ status: true }));
      await dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));
      
      // Pre-fill customer
      if (poData.existingCustomer) {
        form.setFieldsValue({ customer_id: poData.existingCustomer.id });
      } else if (poData.newCustomers && poData.newCustomers.length > 0) {
        // Wait a bit for Redux state to update, then try to find the newly added customer
        // Wait a bit for Redux state to update, then try to find the newly added customer
        setTimeout(() => {
          // Get fresh state from Redux store
          const state = store.getState();
          const updatedCustomers = state.customers?.searchList || state.customers?.list || [];
          const newCustomer = updatedCustomers.find(c => 
            c.name && c.name.toLowerCase() === poData.newCustomers[0].name.toLowerCase()
          );
          if (newCustomer) {
            form.setFieldsValue({ customer_id: newCustomer.id });
          } else {
            // If still not found, show a message
            message.info(`Customer "${poData.newCustomers[0].name}" not found. Please select manually.`);
          }
        }, 1500);
      }

      // Pre-fill order date
      if (poData.orderDate) {
        setOrderDate(moment(poData.orderDate));
        form.setFieldsValue({ order_date: moment(poData.orderDate) });
      }

      // Pre-fill order number in note (if available)
      if (poData.orderNumber) {
        form.setFieldsValue({ note: `Customer PO: ${poData.orderNumber}` });
      }

      // Pre-fill products
      const productsToAdd = [];
      
      // Add existing products
      if (poData.existingProducts && poData.existingProducts.length > 0) {
        for (const product of poData.existingProducts) {
          // Use MRP (Maximum Retail Price) - price before discount
          // Support both 'mrp' (new format) and 'price'/'extractedPrice' (backward compatibility)
          const mrp = product.mrp || product.extractedPrice || product.price || product.sale_price || 0;
          const extractedDiscount = product.discount || product.extractedDiscount || 0;
          const extractedQuantity = product.quantity || product.extractedQuantity || 1;
          
          const itemTotal = mrp * extractedQuantity;
          const itemDiscount = (itemTotal * extractedDiscount) / 100;
          const itemAfterProductDiscount = itemTotal - itemDiscount;
          const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
          
          productsToAdd.push({
            key: productsToAdd.length,
            id: product.id,
            name: product.name,
            isbn: product.isbn,
            author: product.author || "",
            book_publisher_id: product.book_publisher_id || null,
            book_publisher: product.book_publisher || null,
            ordered_quantity: extractedQuantity,
            fulfilled_quantity: 0,
            sale_price: mrp, // MRP goes into sale_price field
            discount: extractedDiscount,
            total_amount: itemAfterProductDiscount - billDiscountAmount,
          });
        }
      }

      // Add new products (those without product_id)
      if (poData.newProducts && poData.newProducts.length > 0) {
        for (const product of poData.newProducts) {
          // Generate temporary ISBN if not available
          let isbn = product.isbn;
          if (!isbn || !isbn.trim()) {
            const existingTempISBNs = productsToAdd
              .filter(p => p.isbn && p.isbn.startsWith('NA'))
              .map(p => {
                const match = p.isbn.match(/^NA(\d+)$/);
                return match ? parseInt(match[1]) : 0;
              });
            const maxNum = existingTempISBNs.length > 0 ? Math.max(...existingTempISBNs) : 0;
            isbn = `NA${maxNum + 1}`;
          }

          // Try to match publisher from current publishers list
          let publisherId = null;
          let publisher = null;
          if (product.publisher) {
            const matchedPublisher = allPublishers.find(p => 
              p.name.toLowerCase() === product.publisher.toLowerCase()
            );
            if (matchedPublisher) {
              publisherId = matchedPublisher.id;
              publisher = matchedPublisher;
            }
          }

          // Use MRP (Maximum Retail Price) - price before discount
          // Support both 'mrp' (new format) and 'price' (backward compatibility)
          // If only discountedPrice is available, calculate MRP backwards
          let mrp = product.mrp || product.price || 0;
          const extractedDiscount = product.discount || 0;
          const extractedQuantity = product.quantity || 1;
          
          // If MRP is not available but discountedPrice and discount are, calculate MRP backwards
          if (!mrp && product.discountedPrice && extractedDiscount > 0) {
            mrp = product.discountedPrice / (1 - extractedDiscount / 100);
          }
          
          const itemTotal = mrp * extractedQuantity;
          const itemDiscount = (itemTotal * extractedDiscount) / 100;
          const itemAfterProductDiscount = itemTotal - itemDiscount;
          const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
          
          productsToAdd.push({
            key: productsToAdd.length,
            isbn: isbn.trim(),
            name: product.title || product.name || "",
            author: product.author || "",
            book_publisher_id: publisherId,
            book_publisher: publisher,
            id: null,
            ordered_quantity: extractedQuantity,
            fulfilled_quantity: 0,
            sale_price: mrp, // MRP goes into sale_price field
            discount: extractedDiscount,
            total_amount: itemAfterProductDiscount - billDiscountAmount,
            isNewProduct: true,
            hasTemporaryISBN: isbn.startsWith('NA'),
          });
        }
      }

      if (productsToAdd.length > 0) {
        setSelectedProducts(productsToAdd);
        calculateTotal(productsToAdd, discount);
        message.success(`Pre-filled ${productsToAdd.length} product(s) from customer PO`);
      }

      setCustomerPOUploadVisible(false);
    } catch (error) {
      console.error('Error pre-filling form:', error);
      message.error('Failed to pre-fill form data');
    }
  };

  // Check if ISBN exists in database
  const checkISBNExists = async (isbn) => {
    if (!isbn || !isbn.trim()) return null;
    
    try {
      const result = await dispatch(globalProductSearch(isbn.trim(), 1, 10));
      if (result && result.status === "success" && result.data) {
        const exactMatch = result.data.find(p => p.isbn === isbn.trim());
        return exactMatch || null;
      }
      return null;
    } catch (error) {
      console.error('Error checking ISBN:', error);
      return null;
    }
  };

  // Handle ISBN input
  const handleISBNSearch = async (value) => {
    setIsbnInput(value);
    if (value && value.trim()) {
      const result = await dispatch(globalProductSearch(value.trim(), 1, 10));
      if (result && result.status === "success" && result.data) {
        setIsbnSearchResults(result.data.map(p => ({
          value: p.isbn,
          label: `${p.isbn} - ${p.name}`,
          product: p
        })));
      }
    } else {
      setIsbnSearchResults([]);
    }
  };

  const handleISBNSelect = async (value, option) => {
    if (option && option.product) {
      // Product exists - add it
      handleAddProduct(option.product);
    } else {
      // Product doesn't exist - create manual entry
      handleAddNewProductByISBN(value);
    }
    setIsbnInput("");
    setIsbnSearchResults([]);
  };

  const handleRemoveProduct = (key) => {
    const updated = selectedProducts.filter((p) => p.key !== key);
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
  };

  const handleQuantityChange = (key, value) => {
    const updated = selectedProducts.map((p) => {
      if (p.key === key) {
        const qty = value || 1;
        const itemTotal = p.sale_price * qty;
        const itemDiscount = (itemTotal * (p.discount || 0)) / 100;
        const itemAfterProductDiscount = itemTotal - itemDiscount;
        const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
        return {
          ...p,
          ordered_quantity: qty,
          total_amount: itemAfterProductDiscount - billDiscountAmount,
        };
      }
      return p;
    });
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
  };

  const handlePriceChange = (key, value) => {
    const updated = selectedProducts.map((p) => {
      if (p.key === key) {
        const price = value || 0;
        const itemTotal = price * p.ordered_quantity;
        const itemDiscount = (itemTotal * (p.discount || 0)) / 100;
        const itemAfterProductDiscount = itemTotal - itemDiscount;
        const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
        return {
          ...p,
          sale_price: price,
          total_amount: itemAfterProductDiscount - billDiscountAmount,
        };
      }
      return p;
    });
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
  };

  const handleDiscountChange = (key, value) => {
    const updated = selectedProducts.map((p) => {
      if (p.key === key) {
        const disc = value || 0;
        const itemTotal = p.sale_price * p.ordered_quantity;
        const itemDiscount = (itemTotal * disc) / 100;
        const itemAfterProductDiscount = itemTotal - itemDiscount;
        const billDiscountAmount = (itemAfterProductDiscount * discount) / 100;
        return {
          ...p,
          discount: disc,
          total_amount: itemAfterProductDiscount - billDiscountAmount,
        };
      }
      return p;
    });
    setSelectedProducts(updated);
    calculateTotal(updated, discount);
  };

  const handleBillDiscountChange = (value) => {
    const billDiscountPercent = value || 0;
    setDiscount(billDiscountPercent);
    
    // Apply bill discount percentage to all products
    const updated = selectedProducts.map((p) => {
      const itemTotal = p.sale_price * p.ordered_quantity;
      const itemDiscount = (itemTotal * (p.discount || 0)) / 100;
      const itemAfterProductDiscount = itemTotal - itemDiscount;
      const billDiscountAmount = (itemAfterProductDiscount * billDiscountPercent) / 100;
      return {
        ...p,
        total_amount: itemAfterProductDiscount - billDiscountAmount,
      };
    });
    setSelectedProducts(updated);
    calculateTotal(updated, billDiscountPercent);
  };

  const calculateTotal = (products, billDiscountPercent) => {
    const subtotal = products.reduce((sum, p) => sum + (p.total_amount || 0), 0);
    setTotalAmount(subtotal);
  };

  const handleSubmit = async (values) => {
    if (selectedProducts.length === 0) {
      message.error("Please add at least one product");
      return;
    }

    const orderItems = [];
    for (const p of selectedProducts) {
      if (p.isNewProduct || !p.id) {
        let isbn = p.isbn;
        if (!isbn || !isbn.trim()) {
          isbn = await fetchNextTempISBN();
        }
        orderItems.push({
          isbn: isbn.trim(),
          name: p.name || p.product_name || "",
          author: p.author || "",
          book_publisher_id: p.book_publisher_id || null,
          ordered_quantity: p.ordered_quantity,
          sale_price: p.sale_price,
          discount: p.discount || 0,
        });
      } else {
        orderItems.push({
          product_id: p.id,
          ordered_quantity: p.ordered_quantity,
          sale_price: p.sale_price,
          discount: p.discount || 0,
        });
      }
    }

    const orderData = {
      order_date: orderDate.format("YYYY-MM-DD"),
      customer_id: values.customer_id,
      user_id: values.user_id,
      discount: discount || 0,
      note: values.note || "",
      order_items: orderItems,
    };

    if (isEdit) {
      const result = await dispatch(updateSalesOrder(id, orderData));
      if (result?.message === "success") {
        navigate("/sales-order");
      }
    } else {
      const result = await dispatch(createSalesOrder(orderData));
      if (result?.message === "success") {
        navigate("/sales-order");
      }
    }
  };

  const productColumns = [
    {
      title: "Product",
      dataIndex: "name",
      key: "name",
      render: (text, record) => {
        if (record.isNewProduct) {
          // For new products, show editable fields
          return (
            <div>
              <div style={{ marginBottom: "4px" }}>
                <Input
                  placeholder="ISBN (leave empty for temporary ISBN)"
                  value={record.isbn || ""}
                  onChange={async (e) => {
                    const value = e.target.value.trim();
                    // If user clears ISBN, generate temporary one
                    if (!value && record.hasTemporaryISBN === false) {
                      const tempISBN = await fetchNextTempISBN();
                      handleUpdateNewProductField(record.key, "isbn", tempISBN);
                      handleUpdateNewProductField(record.key, "hasTemporaryISBN", true);
                    } else {
                      handleUpdateNewProductField(record.key, "isbn", value);
                      handleUpdateNewProductField(record.key, "hasTemporaryISBN", value.startsWith('NA'));
                    }
                  }}
                  size="small"
                  suffix={
                    record.isbn && record.isbn.startsWith('NA') ? (
                      <span style={{ fontSize: "10px", color: "#ff9800" }} title="Temporary ISBN">TEMP</span>
                    ) : null
                  }
                />
                {record.isbn && record.isbn.startsWith('NA') && (
                  <div style={{ fontSize: "10px", color: "#ff9800", marginTop: "2px" }}>
                    Temporary ISBN - Update when actual ISBN is available
                  </div>
                )}
              </div>
              <Input
                placeholder="Product Name *"
                value={record.name || ""}
                onChange={(e) => handleUpdateNewProductField(record.key, "name", e.target.value)}
                size="small"
                required
                style={{ marginBottom: "4px" }}
              />
              <Input
                placeholder="Author"
                value={record.author || ""}
                onChange={(e) => handleUpdateNewProductField(record.key, "author", e.target.value)}
                size="small"
                style={{ marginBottom: "4px" }}
              />
              <Select
                placeholder="Select or type publisher name"
                value={record.book_publisher_id || undefined}
                onChange={(value) => {
                  if (value) {
                    const publisher = allPublishers.find(p => p.id === value);
                    handleUpdateNewProductField(record.key, "book_publisher_id", value);
                    handleUpdateNewProductField(record.key, "book_publisher", publisher);
                  } else {
                    handleUpdateNewProductField(record.key, "book_publisher_id", null);
                    handleUpdateNewProductField(record.key, "book_publisher", null);
                  }
                }}
                onSearch={(value) => handlePublisherSearch(value, record.key)}
                size="small"
                style={{ width: "100%" }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  if (option.value === "ADD_PUBLISHER") return true;
                  return option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    {publisherSearchValue && 
                     !allPublishers.some(p => p.name.toLowerCase() === publisherSearchValue.toLowerCase()) && (
                      <div style={{ padding: "8px", borderTop: "1px solid #f0f0f0" }}>
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onClick={() => handleAddPublisher(record.key)}
                          style={{ width: "100%", textAlign: "left" }}
                        >
                          Add Publisher: "{publisherSearchValue}"
                        </Button>
                      </div>
                    )}
                  </>
                )}
              >
                {allPublishers.map((publisher) => (
                  <Option key={publisher.id} value={publisher.id}>
                    {publisher.name}
                  </Option>
                ))}
              </Select>
            </div>
          );
        }
        // For existing products, show read-only info
        return (
          <div>
            <div style={{ fontWeight: "500" }}>{text}</div>
            <div style={{ fontSize: "12px", color: "#999", marginTop: "2px" }}>
              ISBN: {record.isbn}
            </div>
            {record.author && (
              <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                Author: {record.author}
              </div>
            )}
            {record.book_publisher?.name && (
              <div style={{ fontSize: "12px", color: "#666", marginTop: "2px" }}>
                Publisher: {record.book_publisher.name}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Quantity",
      dataIndex: "ordered_quantity",
      key: "ordered_quantity",
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={1}
          value={value}
          onChange={(val) => handleQuantityChange(record.key, val)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Price",
      dataIndex: "sale_price",
      key: "sale_price",
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          value={value}
          onChange={(val) => handlePriceChange(record.key, val)}
          style={{ width: "100%" }}
          formatter={(value) => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
          parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
        />
      ),
    },
    {
      title: "Discount %",
      dataIndex: "discount",
      key: "discount",
      width: 120,
      render: (value, record) => (
        <InputNumber
          min={0}
          max={100}
          value={value}
          onChange={(val) => handleDiscountChange(record.key, val)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Total",
      dataIndex: "total_amount",
      key: "total_amount",
      width: 120,
      render: (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.key)}
        />
      ),
    },
  ];

  return (
    <div>
      <Card>
      <Form
        form={form}
        className='m-lg-4'
        name='dynamic_form_nest_item'
        onFinish={handleSubmit}
        onFinishFailed={() => {
          message.error('Please fill all required fields');
        }}
        layout='vertical'
        size='small'
        autoComplete='off'
        style={{ fontSize: '13px' }}>
        <Row className='mr-top' gutter={[16, 16]}>
          <Col span={24} className='border rounded column-design'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#dadada', padding: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/sales-order")} size="small">
                  Back
                </Button> */}
                <Title level={4} style={{ margin: 0 }}>
                  {isEdit ? "Edit Sales Order" : "Create New Sales Order"}
                </Title>
              </div>
              {!isEdit && (
                <Button
                  type="primary"
                  icon={<RobotOutlined />}
                  onClick={() => setCustomerPOUploadVisible(true)}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                  size="small"
                >
                  Upload Customer PO
                </Button>
              )}
            </div>
          </Col>

          {/* Main Fields Row - Mobile Responsive */}
          <Col span={24}>
            <Row gutter={[16, 16]}>
              {/* Order Date */}
              <Col xs={24} sm={12} md={6} lg={3} xl={3}>
                <Form.Item
                  label='Order Date'
                  name="order_date"
                  rules={[{ required: true, message: "Please select order date" }]}
                >
                  <DatePicker
                    onChange={(value) => setOrderDate(value || moment())}
                    value={orderDate}
                    style={{ width: "100%", fontSize: '13px' }}
                    format="DD-MMM-YYYY"
                    placeholder="Order Date"
                  />
                </Form.Item>
              </Col>

              {/* Customer */}
              <Col xs={24} sm={24} md={12} lg={6} xl={4}>
                <Form.Item
                  label='Customer'
                  name='customer_id'
                  rules={[
                    {
                      required: true,
                      message: "Please Select a Customer!",
                    },
                  ]}>
                  <Select
                    loading={!allCustomers.length}
                    showSearch
                    placeholder='Select Customer'
                    optionFilterProp='children'
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ fontSize: '13px', width: '100%' }}
                    dropdownStyle={{ fontSize: '13px' }}
                  >
                    {allCustomers &&
                      allCustomers.map((customer) => (
                        <Option key={customer.id} value={customer.id}>
                          {customer.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Sales Person */}
              <Col xs={24} sm={12} md={6} lg={3} xl={3}>
                <Form.Item
                  label='Sales Person'
                  name='user_id'
                  rules={[
                    {
                      required: true,
                      message: "Please Select a sales person!",
                    },
                  ]}>
                  <Select
                    loading={!allStaff.length}
                    showSearch
                    placeholder='Select Sales Person'
                    optionFilterProp='children'
                    filterOption={(input, option) =>
                      option.children.toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ fontSize: '13px', width: '100%' }}
                    dropdownStyle={{ fontSize: '13px' }}
                  >
                    {allStaff &&
                      allStaff?.map((staff) => (
                        <Option key={staff.id} value={staff.id}>
                          {staff.username}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>

              {/* Bill Discount */}
              <Col xs={24} sm={12} md={6} lg={3} xl={3}>
                <Form.Item label="Bill Discount %">
                  <InputNumber
                    min={0}
                    max={100}
                    value={discount}
                    onChange={handleBillDiscountChange}
                    style={{ width: "100%", fontSize: '13px' }}
                    placeholder="Discount %"
                  />
                </Form.Item>
              </Col>

              {/* Note */}
              <Col xs={24} sm={24} md={12} lg={6} xl={4}>
                <Form.Item label="Note" name="note">
                  <TextArea rows={2} placeholder="Enter any notes..." style={{ fontSize: '13px' }} />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          {/* Product Section */}
          <Col span={24} lg={24}>
            <div style={{ marginBottom: "16px" }}>
              <Row gutter={8}>
                <Col span={12}>
                  <AutoComplete
                    placeholder="Enter ISBN to search or add new product"
                    value={isbnInput}
                    options={isbnSearchResults}
                    onSearch={handleISBNSearch}
                    onSelect={handleISBNSelect}
                    style={{ width: "100%", fontSize: '13px' }}
                    filterOption={false}
                    notFoundContent={
                      isbnInput ? (
                        <Button
                          type="link"
                          icon={<PlusOutlined />}
                          onClick={() => handleAddNewProductByISBN(isbnInput)}
                          style={{ fontSize: '13px' }}
                        >
                          Add New Product: "{isbnInput}"
                        </Button>
                      ) : null
                    }
                  />
                </Col>
                <Col span={6}>
                  <Button
                    type="default"
                    icon={<PlusOutlined />}
                    onClick={handleAddProductWithoutISBN}
                    style={{ width: "100%", fontSize: '13px' }}
                  >
                    Add Without ISBN
                  </Button>
                </Col>
                <Col span={6}>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => setProductSearchVisible(true)}
                    style={{ width: "100%", fontSize: '13px' }}
                  >
                    Browse Products
                  </Button>
                </Col>
              </Row>
            </div>

            {selectedProducts.length > 0 && (
              <Table
                columns={productColumns}
                dataSource={selectedProducts}
                rowKey="key"
                pagination={false}
                scroll={{ x: true }}
                size="small"
              />
            )}
          </Col>

          {/* Totals Section */}
          <Col span={24} lg={16}></Col>
          <Col span={24} lg={8}>
            <div
              style={{
                padding: "10px 20px",
                display: "flex",
                justifyContent: "space-between",
                border: "1px solid #ccc",
              }}>
              <strong>Total Before Discount: </strong>
              <strong>₹{selectedProducts.reduce((sum, p) => {
                return sum + (p.sale_price * p.ordered_quantity);
              }, 0).toFixed(2)}</strong>
            </div>

            <div
              style={{
                padding: "10px 20px",
                display: "flex",
                justifyContent: "space-between",
                border: "1px solid #ccc",
              }}>
              <strong>Subtotal (after product discounts): </strong>
              <strong>₹{selectedProducts.reduce((sum, p) => {
                const itemTotal = p.sale_price * p.ordered_quantity;
                const itemDiscount = (itemTotal * (p.discount || 0)) / 100;
                return sum + (itemTotal - itemDiscount);
              }, 0).toFixed(2)}</strong>
            </div>

            {discount > 0 && (
              <div
                style={{
                  padding: "10px 20px",
                  display: "flex",
                  justifyContent: "space-between",
                  border: "1px solid #ccc",
                }}>
                <strong>Bill Discount ({discount}%): </strong>
                <strong>₹{(() => {
                  const subtotal = selectedProducts.reduce((sum, p) => {
                    const itemTotal = p.sale_price * p.ordered_quantity;
                    const itemDiscount = (itemTotal * (p.discount || 0)) / 100;
                    return sum + (itemTotal - itemDiscount);
                  }, 0);
                  return (subtotal * discount / 100).toFixed(2);
                })()}</strong>
              </div>
            )}

            <div
              style={{
                padding: "10px 20px",
                display: "flex",
                justifyContent: "space-between",
                border: "1px solid #ccc",
              }}>
              <strong>Total: </strong>
              <strong>₹{totalAmount.toFixed(2)}</strong>
            </div>

            <Form.Item style={{ marginTop: "24px" }}>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SaveOutlined />} style={{ fontSize: '13px' }}>
                  {isEdit ? "Update Order" : "Create Order"}
                </Button>
                <Button onClick={() => navigate("/sales-order")} style={{ fontSize: '13px' }}>Cancel</Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>

    <ProductSearchModal
        visible={productSearchVisible}
        onClose={() => setProductSearchVisible(false)}
        onProductSelect={handleAddProduct}
        allProducts={allProducts}
        selectedProds={selectedProducts}
      />

      <Modal
        title="Add New Publisher"
        open={publisherModalVisible}
        onCancel={() => {
          setPublisherModalVisible(false);
          setPublisherSearchValue("");
          setCurrentProductKey(null);
        }}
        footer={null}
        width={600}
      >
        <AddBookPublisher
          inModal={true}
          prefillText={publisherSearchValue}
          onSuccess={handlePublisherAdded}
          onCancel={() => {
            setPublisherModalVisible(false);
            setPublisherSearchValue("");
            setCurrentProductKey(null);
          }}
        />
      </Modal>

      {customerPOUploadVisible && (
        <CustomerPOUpload
          onDataExtracted={handleCustomerPODataExtracted}
          onClose={() => setCustomerPOUploadVisible(false)}
        />
      )}
    </div>
  );
};

export default AddSalesOrder;
